import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { api } from "./lib/api";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute, AdminRoute, SupportRoute } from "./components/ProtectedRoute";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import { TermsPage, PrivacyPage, LegalPage, CommercialTermsPage } from "./pages/Legal";
import FindParking from "./pages/FindParking";
import SpotDetail from "./pages/SpotDetail";
import Dashboard from "./pages/Dashboard";
import Bookings from "./pages/Bookings";
import BookingDetail from "./pages/BookingDetail";
import Profile from "./pages/Profile";
import MyVehicles from "./pages/MyVehicles";
import Invoices from "./pages/Invoices";
import HostInvoices from "./pages/HostInvoices";
import VerifyEmail from "./pages/VerifyEmail";
import Notifications from "./pages/Notifications";
import Support from "./pages/Support";
import SupportTicketDetail from "./pages/SupportTicketDetail";
import HostDashboard from "./pages/HostDashboard";
import HostAvailability from "./pages/HostAvailability";
import HostNewListing from "./pages/HostNewListing";
import HostEditListing from "./pages/HostEditListing";
import HostBookings from "./pages/HostBookings";
import Payouts from "./pages/Payouts";
import Wallet from "./pages/Wallet";
import SupportConsole from "./pages/SupportConsole";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminHosts from "./pages/admin/AdminHosts";
import AdminDrivers from "./pages/admin/AdminDrivers";
import AdminListings from "./pages/admin/AdminListings";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminReports from "./pages/admin/AdminReports";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminBroadcast from "./pages/admin/AdminBroadcast";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminCommission from "./pages/admin/AdminCommission";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminDeletions from "./pages/admin/AdminDeletions";
import AdminConsents from "./pages/admin/AdminConsents";
import AdminTicketDetail from "./pages/admin/AdminTicketDetail";
import AdminCommercial from "./pages/admin/AdminCommercial";
import SpinReveal from "./pages/SpinReveal";
import NotFound from "./pages/NotFound";
import TermsGate from "./components/TermsGate";
import SecurityHome from "./pages/security/SecurityHome";
import SecurityDashboard from "./pages/security/SecurityDashboard";
import PropertyLanding from "./pages/PropertyLanding";
import CommercialDashboard from "./pages/commercial/CommercialDashboard";
import CommercialProperties from "./pages/commercial/CommercialProperties";
import CommercialBookings from "./pages/commercial/CommercialBookings";
import PropertyNew from "./pages/commercial/PropertyNew";
import PropertyDetail from "./pages/commercial/PropertyDetail";

function ReferralTracker() {
  // Server-side referral attribution (?ref=MRP-XXXXXX) — recorded via API + httpOnly
  // cookie, never localStorage-only. Once per code per browser session.
  const { search } = useLocation();
  useEffect(() => {
    const code = new URLSearchParams(search).get("ref");
    if (!code) return;
    const key = `mrp_ref_tracked_${code.toUpperCase()}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.referralsTrack(code.toUpperCase()).catch(() => {});
  }, [search]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/welcome/reward" element={<ProtectedRoute><SpinReveal /></ProtectedRoute>} />
      <Route path="/auth/email" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/commercial-terms" element={<CommercialTermsPage />} />
      <Route path="/find" element={<FindParking />} />
      <Route path="/spot/:id" element={<SpotDetail />} />
      <Route path="/p/:code" element={<PropertyLanding />} />
      <Route path="/p/:code/:categoryCode" element={<PropertyLanding />} />
      <Route path="/security" element={<ProtectedRoute><SecurityHome /></ProtectedRoute>} />
      <Route path="/security/:propertyId" element={<ProtectedRoute><SecurityDashboard /></ProtectedRoute>} />
      <Route path="/commercial" element={<ProtectedRoute><CommercialDashboard /></ProtectedRoute>} />
      <Route path="/commercial/properties" element={<ProtectedRoute><CommercialProperties /></ProtectedRoute>} />
      <Route path="/commercial/bookings" element={<ProtectedRoute><CommercialBookings /></ProtectedRoute>} />
      <Route path="/commercial/properties/new" element={<ProtectedRoute><PropertyNew /></ProtectedRoute>} />
      <Route path="/commercial/properties/:id" element={<ProtectedRoute><PropertyDetail /></ProtectedRoute>} />

      {/* Authenticated */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
      <Route path="/bookings/:id" element={<ProtectedRoute><BookingDetail /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/vehicles" element={<ProtectedRoute><MyVehicles /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/host/invoices" element={<ProtectedRoute><HostInvoices /></ProtectedRoute>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/support" element={<Support />} />
      <Route path="/support/tickets/:id" element={<ProtectedRoute><SupportTicketDetail /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />

      {/* Host */}
      <Route path="/host" element={<ProtectedRoute><HostDashboard /></ProtectedRoute>} />
      <Route path="/host/new" element={<Navigate to="/host/listings/new" replace />} />
      <Route path="/host/listings/new" element={<ProtectedRoute><HostNewListing /></ProtectedRoute>} />
      <Route path="/host/listings/:id/edit" element={<ProtectedRoute><HostEditListing /></ProtectedRoute>} />
      <Route path="/host/bookings" element={<ProtectedRoute><HostBookings /></ProtectedRoute>} />
      <Route path="/host/availability" element={<ProtectedRoute><HostAvailability /></ProtectedRoute>} />
      <Route path="/payouts" element={<ProtectedRoute><Payouts /></ProtectedRoute>} />

      {/* RBAC */}
      <Route path="/support-console" element={<SupportRoute><SupportConsole /></SupportRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="hosts" element={<AdminHosts />} />
        <Route path="drivers" element={<AdminDrivers />} />
        <Route path="listings" element={<AdminListings />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="promotions" element={<AdminPromotions />} />
        <Route path="commission" element={<AdminCommission />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="deletions" element={<AdminDeletions />} />
        <Route path="consents" element={<AdminConsents />} />
        <Route path="commercial" element={<AdminCommercial />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="tickets/:id" element={<AdminTicketDetail />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="broadcast" element={<AdminBroadcast />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App bg-[#080B0D] min-h-screen text-white">
      <BrowserRouter>
        <AuthProvider>
          <ReferralTracker />
          <TermsGate />
          <AppRouter />
          <Toaster theme="dark" position="top-center" toastOptions={{ style: { background: "#1F2328", border: "1px solid #27272A", color: "#fff" } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
